import React from 'react';
import moment from 'moment';
import { inject, observer } from 'mobx-react';
import { AxiosError } from 'axios';
import exportResponseError from '@utils/exportResponseError';
import { IReport } from '@components/sources/sourceDetails/cloudSourceDetails/cloudSourceReports/Report';
import { apiAuthCaller } from '@services/apiCaller';
import { ISyncStatus } from '@components/sources/sourceDetails/cloudSourceDetails/cloudSourceStatusTab/cloudSourceSyncStatus';
import SourceFactory from '@components/sources/sourceFactory';
import { CatchErr, FixMe } from '@utils/types';
import { orderBy, uniqBy } from 'lodash';
import {
  convertToHourlyUTC,
  getScheduleDetails,
} from '@components/sources/visualizer/mappingScreens/sourceUtil';
import { ISource } from '../source';
import { IDestination } from '../destination';
import { IRootStore } from '..';
import { SOURCE_CATEGORIES } from '@components/common/constants';
import { getApiErrorMessage } from '@components/common/util/util';
import {
  fetchPendingJobsReports,
  getLastRunAndCustomResources,
  getReports,
  ICustomResources,
  isSyncing,
  startSync,
  stopSync,
} from './util';

export interface ICloudSourceRunStatus {
  jobId: string;
  jobRunId: string;
  taskId: string;
  taskRunId: string;
  error: string;
  startedAt: string;
  finishedAt: string;
}
interface CloudSourceProps {
  source: ISource;
  destinations: IDestination[];
}
interface CloudSourceState {
  lastRunData: ICloudSourceRunStatus[] | IReport[] | null;
  runHistory: ICloudSourceRunStatus[] | IReport[] | null;
  starting: boolean;
  stopping: boolean;
  stopped: boolean;
  status: ISyncStatus | null;
  state: string;
  customResources: ICustomResources | null;
  loadingReports: boolean;
  canLoadMore: boolean;
}

interface CloudSourceContextState {
  isSyncing: boolean;
  status: ISyncStatus | null;
  lastRunData: ICloudSourceRunStatus[] | IReport[] | null;
  reportsEnabled?: boolean;
  reportsViewEnabled?: boolean;
  stopping: boolean;
  stopped: boolean;
  starting: boolean;
  isPaused: boolean;
  state: string;
  nextSyncAt: string;
  noConnections: boolean;
  role: string;
  source: Partial<ISource>;
  runHistory: ICloudSourceRunStatus[] | IReport[] | null;
  loadingReports: boolean;
  canLoadMore: boolean;
  getRunHistory: (from: string, to?: string, destinationId?: string) => Promise<void>;
  // start or stop the sync according to pipeline state
  startStopSync: (e?: React.SyntheticEvent, syncType?: string) => void;
  getDownDirectionRunHistory: (isReplaceOldReports: boolean) => Promise<void>;
  initializeUpAndDownDirectionRunHistory: (
    destinationId: string,
    from: string,
    to: string,
  ) => Promise<void>;
}

enum SyncStatus {
  Syncing = 'syncing',
}

let lastUpDirectionFetchHistoryEndTime: string | undefined;
const cloudPollingTime = 40 * 1000;
const cloudActivePipelinePollingTime = 5 * 1000;
const reportsFetchingTimeout = 120000;
const rETLUpDirectionPollingTime = 120 * 1000;
const rETLPendingJobsPollingTime = 60 * 1000;
const rETLDownDirectionRespLimit = 10;

const isUpDirectionEnabled = (to: string) =>
  moment(to).toDate().toDateString() === moment().toDate().toDateString();

const CloudSourceContext = React.createContext<CloudSourceContextState>({
  isSyncing: false,
  nextSyncAt: '',
  status: null,
  lastRunData: null,
  runHistory: null,
  stopping: false,
  stopped: false,
  starting: false,
  isPaused: false,
  noConnections: false,
  state: '',
  role: '',
  loadingReports: true,
  canLoadMore: true,
  startStopSync: () => null,
  getRunHistory: () => Promise.resolve(),
  source: {},
  getDownDirectionRunHistory: () => Promise.resolve(),
  initializeUpAndDownDirectionRunHistory: () => Promise.resolve(),
});

export const useCloudSourceInfo = () =>
  React.useContext(CloudSourceContext) as CloudSourceContextState;
export interface WithCloudSourcePipelineProps {
  cloudSource: CloudSourceContextState;
}

type CloudSourceStore = Partial<Pick<IRootStore, 'messagesStore' | 'userStore' | 'workspaceStore'>>;

@inject('messagesStore', 'userStore', 'workspaceStore')
@observer
class CloudSource extends React.PureComponent<
  CloudSourceProps & CloudSourceStore,
  CloudSourceState
> {
  constructor(props: CloudSourceProps & CloudSourceStore) {
    super(props);
    this.state = {
      starting: false,
      stopping: false,
      stopped: false,
      status: null,
      lastRunData: null,
      runHistory: null,
      customResources: null,
      state: '',
      loadingReports: false,
      canLoadMore: true,
    };
  }

  timer: FixMe = 0;

  pendingJobsTimer: FixMe = 0;

  startUpDirectionRETLPolling = async (restart: boolean) => {
    if (restart) {
      await this.getUpDirectionRunHistory();
    }

    this.timer = setTimeout(async () => {
      if (isUpDirectionEnabled(this.lastHistoryArgs.to)) {
        await this.getUpDirectionRunHistory();
        this.startUpDirectionRETLPolling(false);
      }
    }, rETLUpDirectionPollingTime);
  };

  startCloudPolling = async () => {
    const { isSyncing, starting, stopping } = this.getPipelineStatuses();
    const pollTime =
      isSyncing || stopping || starting ? cloudActivePipelinePollingTime : cloudPollingTime;

    this.timer = setTimeout(async () => {
      await this.getLastRun();
      this.startCloudPolling();
    }, pollTime);
  };

  initializeCloudPolling = async () => {
    clearTimeout(this.timer);
    await this.getLastRun();

    const { startCloudPolling } = this.getHelpers();

    if (startCloudPolling) {
      startCloudPolling();
    }
  };

  lastHistoryArgs: FixMe = {};

  pendingJobsPolling = async () => {
    const { source, userStore, workspaceStore } = this.props;
    this.resetPendingJobsPolling();
    const { destinations } = this.props;
    const pendingJobRunIds: string[] = [];
    let minStartedAt = '';
    this.state.runHistory?.forEach((job) => {
      if (isSyncing(job.startedAt, job.finishedAt, true)) {
        pendingJobRunIds.push(job.jobRunId);
        if (!minStartedAt || minStartedAt > job.startedAt) {
          minStartedAt = job.startedAt;
        }
      }
    });

    const destination = destinations.find((d) => d.enabled);
    const currentTime = moment();

    this.pendingJobsTimer = setTimeout(async () => {
      if (pendingJobRunIds.length > 0) {
        const reports: IReport[] = await fetchPendingJobsReports(
          {
            pending: pendingJobRunIds,
            destinationId: destination?.id,
            from: minStartedAt,
            to: moment(this.lastHistoryArgs.to).isAfter(currentTime)
              ? currentTime.toISOString()
              : this.lastHistoryArgs.to,
          },
          source,
          userStore?.token || '',
          workspaceStore?.id || '',
        );

        const updateRunHistory =
          (this.state.runHistory || [])?.map((mp) => {
            const updatedJobRun = reports.find(({ jobRunId }) => jobRunId === mp.jobRunId);
            if (updatedJobRun) {
              return updatedJobRun;
            }
            return mp;
          }) || [];

        this.setState({
          runHistory: updateRunHistory,
        });
      }
      this.pendingJobsPolling();
    }, rETLPendingJobsPollingTime);
  };

  resetPendingJobsPolling = () => {
    clearTimeout(this.pendingJobsTimer);

    this.pendingJobsTimer = 0;
  };

  getHelpers = () => {
    const { source } = this.props;

    if (source.enabled) {
      let resetPolling;
      if (source.sourceDef.category === SOURCE_CATEGORIES.WAREHOUSE) {
        resetPolling = this.resetUpDirectionRETLPolling;
      } else {
        resetPolling = this.resetCloudPolling;
      }

      return {
        startUpDirectionRETLPolling: this.startUpDirectionRETLPolling,
        startCloudPolling: this.startCloudPolling,
        pendingJobsPolling: this.pendingJobsPolling,
        resetPolling,
      };
    }
    return {};
  };

  /* reset is about re initiating the UP DIRECTION polling.
  It has no relation with changing of the UP DIRECTION api call params.
  */
  resetUpDirectionRETLPolling = () => {
    clearTimeout(this.timer);
    this.timer = 0;
    this.startUpDirectionRETLPolling(true);
  };

  resetCloudPolling = () => {
    clearTimeout(this.timer);
    this.timer = 0;
    this.startCloudPolling();
  };

  componentWillUnmount() {
    clearTimeout(this.timer);
    const { reportsViewEnabled } = this.isReportsEnabled();
    if (reportsViewEnabled) {
      clearTimeout(this.pendingJobsTimer);
    }
  }

  static getDerivedStateFromProps(props: FixMe, state: FixMe) {
    const { source, destinations, workspaceStore } = props;
    if (!source) {
      return state;
    }
    const sourceInstance = SourceFactory(source?.sourceDef?.category);
    const { lastRunData, stopping, runHistory, starting } = state;
    let lastStatus = lastRunData?.find(
      (data: ICloudSourceRunStatus) => !data.taskId && !data.taskRunId,
    );
    if (!lastStatus) {
      lastStatus = lastRunData && lastRunData[0];
    }
    const { reportsViewEnabled } = {
      reportsViewEnabled: workspaceStore?.enableReports && sourceInstance.isReportsViewEnabled(),
    };

    const newState: Partial<CloudSourceState> = {};

    const ifJobPending = reportsViewEnabled
      ? !!runHistory &&
        runHistory.some((job: FixMe) =>
          job.snapshotFinishedAt
            ? isSyncing(job.startedAt, job.snapshotFinishedAt, false)
            : isSyncing(job.startedAt, job.finishedAt, !stopping),
        )
      : lastStatus &&
        isSyncing(
          lastStatus.startedAt,
          lastStatus.finishedAt,
          sourceInstance.isSyncDelayEnabled() && !stopping,
        );

    if (
      !source?.enabled ||
      (destinations.length > 0 && destinations.every((dest: IDestination) => !dest.enabled))
    ) {
      newState.status = 'paused';
    } else if ((starting && !reportsViewEnabled) || ifJobPending) {
      newState.status = 'syncing';
      newState.starting = false;
    } else {
      newState.status = null;
      newState.stopping = false;
    }

    return Object.keys(newState).length > 0 ? newState : null;
  }

  async componentDidMount() {
    const { reportsViewEnabled } = this.isReportsEnabled();
    if (!reportsViewEnabled) {
      await this.initializeCloudPolling();
    }
  }

  isReportsEnabled = () => {
    const { source, workspaceStore } = this.props;
    return {
      reportsEnabled: workspaceStore?.enableReports,
      reportsViewEnabled:
        workspaceStore?.enableReports &&
        SourceFactory(source?.sourceDef?.category).isReportsViewEnabled(),
    };
  };

  // cloud
  getLastRun = async () => {
    const { source, userStore, workspaceStore } = this.props;
    if (!userStore) {
      return;
    }
    try {
      const { updatedCustomResources, lastRunData } = await getLastRunAndCustomResources(
        source,
        userStore.token,
        this.state.customResources,
        workspaceStore?.id || '',
      );

      this.setState({
        lastRunData,
        customResources: updatedCustomResources,
      });
    } catch (err) {}
  };

  componentDidUpdate(prevProps: FixMe, prevState: FixMe) {
    const { reportsViewEnabled } = this.isReportsEnabled();

    if (!reportsViewEnabled && prevState.status === 'syncing' && this.state.status !== 'syncing') {
      this.getRunHistory();
    }
  }

  // cloud
  getRunHistory = async (
    from: string = this.lastHistoryArgs.from,
    to: string = this.lastHistoryArgs.to,
    destinationId: string = this.lastHistoryArgs.destinationId,
  ) => {
    const { source, userStore, workspaceStore } = this.props;

    this.lastHistoryArgs = { from, to, destinationId };
    if (!from) {
      return;
    }
    try {
      const url = `/reports/workspaces/${userStore?.selectedWorkspaceId}/cloudSources/status/${
        source?.id
      }?from=${convertToHourlyUTC(from, 'floor')}&resources=true&workspaceId=${workspaceStore?.id}`;

      const response = await apiAuthCaller(userStore!.token, undefined, reportsFetchingTimeout).get(
        url,
      );
      this.setState({
        runHistory: response.data,
      });
    } catch (err) {}
  };

  // rETL
  getUpDirectionRunHistory = async () => {
    const { from, to, destinationId, boundary } = this.lastHistoryArgs;
    if (moment(from).isSameOrAfter(to)) {
      return;
    }
    const { source, userStore, workspaceStore } = this.props;
    try {
      const currentTime = moment();

      const periodEndTime = moment(to).isAfter(currentTime)
        ? currentTime.toISOString()
        : moment(to).toISOString();
      const periodStartTime = lastUpDirectionFetchHistoryEndTime || boundary;

      const reports = await getReports(
        {
          from: periodStartTime,
          to: periodEndTime,
          destinationId,
        },
        source,
        userStore?.token || '',
        workspaceStore?.id || '',
      );
      this.setState((prevState) => ({
        runHistory: this.replaceAndAddJobReports(
          reports,
          !lastUpDirectionFetchHistoryEndTime,
          'end',
        ),
        status: SyncStatus.Syncing,
        starting: prevState.starting ? false : prevState.starting,
      }));
    } catch (err) {
      this.setState({
        status: null,
        stopping: false,
        starting: false,
      });
    }
  };

  // rETL
  replaceAndAddJobReports = (
    newReports: IReport[],
    removeExistingReports: boolean,
    pushAtLocation: 'front' | 'end',
  ) => {
    const { runHistory } = this.state;
    const existingReports = (removeExistingReports ? [] : runHistory) || [];
    const { from, to } = this.lastHistoryArgs;
    const reportsCombination = (
      pushAtLocation === 'end'
        ? [...existingReports, ...newReports]
        : [...newReports, ...existingReports]
    ).filter(({ startedAt }) => moment(startedAt).isBetween(from, to, 'date', '[]'));

    return orderBy(uniqBy(reportsCombination, 'jobRunId'), 'startedAt', 'desc');
  };

  // cloud and rETL
  onSync = async (e?: React.SyntheticEvent, syncType?: string) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const { source, userStore } = this.props;

    const inProgress = this.state.status === 'syncing';
    try {
      if (!inProgress) {
        this.setState({
          starting: true,
        });
        const changeSyncType = SourceFactory(source.category).getSyncType(syncType);
        await startSync(source, userStore?.token || '', changeSyncType);
        const { resetPolling } = this.getHelpers();
        if (resetPolling) {
          resetPolling();
        }
      } else {
        this.setState({
          starting: false,
          stopping: true,
        });
        await stopSync(source.id, userStore?.token || '');

        this.setState({
          stopped: true,
        });
      }
    } catch (err: CatchErr) {
      this.setState({
        starting: false,
        stopping: false,
      });
      this.handleError(err);
    }
  };

  // cloud and rETL
  getPipelineStatuses = () => {
    const { stopping, starting, status, stopped, state } = this.state;
    const isSyncing = status === 'syncing';
    const isPaused = status === 'paused';
    return {
      isPaused,
      stopped,
      isSyncing,
      stopping,
      starting,
      status,
      state,
    };
  };

  // cloud and rETL
  handleError = (err: Error | AxiosError) => {
    const { messagesStore } = this.props;
    const errorResponse = exportResponseError(err);
    let errorMessage = errorResponse?.message;
    if (errorResponse?.status === 404) {
      errorMessage = 'Pipeline not found';
    } else {
      errorMessage = 'Something went wrong, please try again later';
    }
    messagesStore!.showErrorMessage(errorMessage);
  };

  // Runs only on date change
  initializeUpAndDownDirectionRunHistory = async (
    destinationId: string,
    from: string = this.lastHistoryArgs.from,
    to: string = this.lastHistoryArgs.to,
  ) => {
    if (moment(from).isAfter(to)) {
      return;
    }

    lastUpDirectionFetchHistoryEndTime = moment().toISOString();
    const boundary = moment(to).isAfter(moment()) ? moment().toISOString() : undefined;
    this.lastHistoryArgs = { from, to, destinationId, offset: 0, boundary };
    clearTimeout(this.timer);
    this.timer = 0;
    const { startUpDirectionRETLPolling } = this.getHelpers();
    if (startUpDirectionRETLPolling) {
      startUpDirectionRETLPolling(false);
    }
    await this.getDownDirectionRunHistory(true);
  };

  //  rETL
  getDownDirectionRunHistory = async (isDateChange: boolean) => {
    if (!this.state.canLoadMore && !isDateChange) {
      return;
    }
    try {
      this.setState({ loadingReports: true, canLoadMore: false });
      const { source, userStore, workspaceStore } = this.props;
      const { offset, boundary, from, to, destinationId } = this.lastHistoryArgs;
      const url = '/reports/report';
      const params = {
        sourceId: source?.id,
        destinationId,
        from: convertToHourlyUTC(from, 'floor'),
        to: boundary ? convertToHourlyUTC(boundary, 'ceil') : convertToHourlyUTC(to, 'ceil'),
        start: offset,
        limit: rETLDownDirectionRespLimit,
        mode: source?.config?.syncBehaviour?.mode,
        workspaceId: workspaceStore?.id,
      };
      const response = await apiAuthCaller(userStore!.token).get(url, { params });

      this.setState(
        {
          runHistory: this.replaceAndAddJobReports(response.data.data, isDateChange, 'front'),
          loadingReports: false,
          canLoadMore: response.data.data.length === rETLDownDirectionRespLimit,
        },
        () => {
          if (isDateChange) {
            const { pendingJobsPolling } = this.getHelpers();
            if (pendingJobsPolling) {
              pendingJobsPolling();
            }
          }
        },
      );
      this.lastHistoryArgs.offset += rETLDownDirectionRespLimit;
    } catch (err) {
      const { messagesStore } = this.props;
      messagesStore?.showErrorMessage(getApiErrorMessage(err, 'Failed to fetch syncs details'));
    }
  };

  render() {
    const { reportsEnabled, reportsViewEnabled } = this.isReportsEnabled();
    const { source, children } = this.props;
    if (!source) {
      return null;
    }
    const { lastRunData, runHistory, loadingReports, canLoadMore } = this.state;
    const statuses = this.getPipelineStatuses();
    const sharedState: CloudSourceContextState =
      {
        ...statuses,
        lastRunData,
        runHistory,
        reportsEnabled,
        reportsViewEnabled,
        source,
        nextSyncAt: source.config.schedule
          ? getScheduleDetails(source.config.schedule).nextSchedule
          : '',
        role: source?.sourceDef.name,
        noConnections: !source?.destinations.length,
        loadingReports,
        canLoadMore,
        startStopSync: this.onSync,
        getRunHistory: this.getRunHistory,
        getDownDirectionRunHistory: this.getDownDirectionRunHistory,
        initializeUpAndDownDirectionRunHistory: this.initializeUpAndDownDirectionRunHistory,
      } || null;

    return (
      <CloudSourceContext.Provider value={sharedState}>{children}</CloudSourceContext.Provider>
    );
  }
}

export default CloudSource as React.ComponentClass<CloudSourceProps>;
